import React from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import ModalCenter from '@/common/components/modal/ModalCenter';
// Vite에서 마크다운 파일을 raw 문자열로 불러오기
import termsMd from '@/constants/terms/v2/terms.md?raw';
import privacyPolicyMd from '@/constants/terms/v1/privacyPolicy.md?raw';

/**
 * 약관 내용을 보여주는 모달 컴포넌트
 */

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy' | 'marketing';
}

const TermsModal: React.FC<TermsModalProps> = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  /**
   * 약관 타입에 따른 제목 반환
   */
  const getTitle = () => {
    switch (type) {
      case 'terms':
        return '서비스 이용약관';
      case 'privacy':
        return '개인정보 처리방침';
      case 'marketing':
        return '마케팅 정보 수신 동의';
      default:
        return '';
    }
  };

  /**
   * 마크다운 형식인지 확인
   */
  const isMarkdown = type === 'terms';

  /**
   * 약관 타입에 따른 내용 반환
   */
  const getContent = () => {
    switch (type) {
      case 'terms':
        return termsMd;
      case 'privacy':
        return privacyPolicyMd;
      case 'marketing':
        return ``;
      default:
        return '';
    }
  };

  return (
    <ModalCenter isOpen={isOpen} onClose={onClose} title={getTitle()} maxWidth="700px">
      <ModalContent>
        <MarkdownContent>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{getContent()}</ReactMarkdown>
        </MarkdownContent>
      </ModalContent>
    </ModalCenter>
  );
};

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const ContentText = styled.pre`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
  font-family: inherit;
`;

const MarkdownContent = styled.div`
  ${typography.paragraph.p2.default}
  color: ${semanticColors.text.secondary};
  line-height: 1.8;
  
  h1 {
    font-size: 1.5rem;
    font-weight: 700;
    color: ${semanticColors.text.primary};
    margin: 2rem 0 1rem 0;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid ${semanticColors.border.brand};
  }
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: ${semanticColors.text.primary};
    margin: 1.5rem 0 0.75rem 0;
  }
  
  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: ${semanticColors.text.secondary};
    margin: 1rem 0 0.5rem 0;
  }
  
  p {
    margin: 0.75rem 0;
  }
  
  ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: disc;
  }
  
  ol {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
    list-style-type: decimal;
  }
  
  li {
    margin: 0.25rem 0;
    display: list-item;
  }
  
  ul ul {
    list-style-type: circle;
  }
  
  ul ul ul {
    list-style-type: square;
  }
  
  hr {
    border: none;
    border-top: 1px solid ${semanticColors.border.primary};
    margin: 1.5rem 0;
  }
  
  strong {
    font-weight: 600;
    color: ${semanticColors.text.primary};
  }
  
  code {
    background: ${semanticColors.background.tertiary};
    padding: 0.125rem 0.375rem;
    border-radius: 4px;
    font-size: 0.875em;
  }
  
  /* 테이블 스타일 */
  table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    font-size: 0.875rem;
  }
  
  thead {
    background: ${semanticColors.background.tertiary};
  }
  
  th, td {
    border: 1px solid ${semanticColors.border.primary};
    padding: 0.75rem 1rem;
    text-align: left;
  }
  
  th {
    font-weight: 600;
    color: ${semanticColors.text.primary};
  }
  
  td {
    color: ${semanticColors.text.secondary};
  }    
`;

export default TermsModal;
